import { ReactElement } from 'react';

import { Node } from '@tiptap/pm/model';
import { EditorState, Transaction } from '@tiptap/pm/state';
import { NodeViewProps } from '@tiptap/react';

import { IconProps } from '@components/icons';
import { BaseIcon } from '@components/icons/base';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMappingOn } from '@components/icons/mapping-on';
import { SelectOption } from '@components/select';
import { useScopedTranslate } from '@locale/translate';
import { generateUniqueCode } from '@utils/strings';
import { MakePropertiesRequired, XOR } from '@utils/types';

import {
  getVariableLabel,
  isTravellerVariableSource,
  isVariableSourceMissing,
  labelSeparator,
} from './field-mapping-variable-label';

import { BaseFieldMappingTextEditorProps } from '../field-mapping-text-editor';
import {
  createPlaceholderHtml,
  createPlaceholderNode,
  FieldMappingContext,
  PlaceholderContainer,
} from '../helpers';
import { EditorValue } from '../text-editor';

export type MappableActionVariable = {
  source: 'action';
  action: string;
  field: string;
  label: string;
  type: FieldType;
  template?: {
    name: string;
    type: FieldType;
    label: string;
  }[];
};

export type MappableObjectVariable = {
  source: 'traveller' | 'traveller_journey' | 'journey' | 'company' | 'company_traveller';
  field: string;
  label: string;
  type: FieldType;
};

export type MappableVariable = MappableActionVariable | MappableObjectVariable;

export type VariableValue = {
  value: string;
  variables?: Record<string, MappableVariable>;
  type?: 'select';
};

export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'date'
  | 'file'
  | 'file_multi'
  | 'options'
  | 'select'
  | 'multi_select'
  | 'number'
  | 'paragraph'
  | 'separator'
  | 'array'
  | 'array_of_strings'
  | 'boolean'
  | 'form'
  | 'select'
  | 'decimal'
  | 'webhookUrl'
  | 'json'
  | 'kennitala'
  | 'user';

export type FormLinkType = 'button' | 'text' | 'url';
export type FormLinkDraft = { variableName: string; linkType: FormLinkType; linkLabel: string };

export const FORM_LINK_FIELD = 'unique_link';
export const DEFAULT_LINK_LABEL = 'Open form';
export const MAX_LINK_LABEL_LENGTH = 255;

export type FieldMappingFieldItem = {
  source: string;
  field: string;
  label: string;
  type?: FieldType;
  parentType?: string;
  action?: string;
  description?: string;
  options?: SelectOption[] | { value: string; display: string }[];
  optionsFromListId?: string | null;
  excludedOptions?: string[];
  icon?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  variable?: string;
  format?: string;
  formatLanguage?: string;
  linkType?: FormLinkType;
  linkLabel?: string;
  isActive?: boolean;
  tooltip?: string;
};

export type FieldMappingGroup = {
  id: string;
  icon?: JSX.Element;
  label: string;
  description?: string;
  fields: FieldMappingFieldItem[];
  action?: string;
};

export type FieldMappingData = XOR<
  {},
  {
    variableGroups: FieldMappingGroup[];
    fieldMappingId: string;
  }
>;

export type FieldMappingVariables = {
  [key in string]: FieldMappingFieldItem;
};

export type FieldMappingEditorValue = EditorValue<{ variables?: FieldMappingVariables }>;

export type ExtendedFieldMappingEditorValue = FieldMappingEditorValue | { value: string };

export type BaseFieldMappingFieldProps<T = ExtendedFieldMappingEditorValue> =
  MakePropertiesRequired<Omit<BaseFieldMappingTextEditorProps, 'onChange'>, 'variableGroups'> & {
    onChange?(value: T): void;
    defaultValue?: T;
  };

export const getVariableName = (inputObject: VariableValue) => {
  if (inputObject.variables) {
    const variable = Object.values(inputObject.variables)[0];
    return extractNameFromVariable(variable);
  }
  return '';
};

export const extractNameFromVariable = (variable: MappableVariable) => {
  if (variable.source === 'action') {
    return `${variable.action}__${variable.field}`;
  } else {
    return `${variable.source}__${variable.field}`;
  }
};

export const isFieldMappingEditorValue = (
  value: ExtendedFieldMappingEditorValue
): value is FieldMappingEditorValue => Boolean((value as FieldMappingEditorValue).variables);

const tag = 'field-mapped-property';

const attributes = [
  'source',
  'label',
  'field',
  'action',
  'type',
  'tooltip',
  'variable',
  'format',
  'formatLanguage',
  'linkType',
  'linkLabel',
];

export const getPlaceholderHtml = createPlaceholderHtml({ tag, attributes });

const NodeComponent = (props: NodeViewProps) => {
  const isSourceMissing = isVariableSourceMissing(props.node.attrs);
  const variant = isSourceMissing ? 'fieldMappedError' : 'fieldMapped';
  const icon = isSourceMissing ? IconMappingOff : IconMappingOn;

  return <PlaceholderContainer variant={variant} icon={icon} {...props} />;
};

export const FieldMappingNode = createPlaceholderNode({
  attributes,
  renderText: getPlaceholderHtml,
  tag,
  nodeComponent: NodeComponent,
});

// Reads a pill element into its {{ }} name + entry. Non-mutating: takes the
// variables collected so far only to keep the generated name unique; the caller
// adds the entry.
const pillToVariable = (element: Element, existingVariables: FieldMappingVariables) => {
  const field = element.attributes.getNamedItem('field')?.nodeValue;
  const source = element.attributes.getNamedItem('source')?.nodeValue;
  const label = element.attributes.getNamedItem('label')?.nodeValue;
  const action = element.attributes.getNamedItem('action')?.nodeValue;
  const type = element.attributes.getNamedItem('type')?.nodeValue;
  const format = element.attributes.getNamedItem('format')?.nodeValue;
  const formatLanguage = element.attributes.getNamedItem('formatLanguage')?.nodeValue;
  const linkType = element.attributes.getNamedItem('linkType')?.nodeValue;
  const linkLabel = element.attributes.getNamedItem('linkLabel')?.nodeValue;
  const storedVariable = element.attributes.getNamedItem('variable')?.nodeValue;
  const baseVariableName =
    source?.startsWith('journey_attribute__') || source?.startsWith('company_traveller_attribute__')
      ? `${source}__${field}`
      : `${source}_${field}`;
  const variableName =
    storedVariable && storedVariable !== 'undefined' && !existingVariables[storedVariable]
      ? storedVariable
      : existingVariables[baseVariableName]
      ? `${baseVariableName}__${generateUniqueCode()}`
      : baseVariableName;
  const variableEntry: FieldMappingFieldItem = {
    source,
    field,
    label,
  } as FieldMappingFieldItem;
  if (action && action !== 'undefined') {
    variableEntry.action = action;
  }
  if (type && type !== 'undefined') {
    variableEntry.type = type as FieldMappingFieldItem['type'];
  }
  if (format && format !== 'undefined') {
    variableEntry.format = format;
  }
  if (formatLanguage && formatLanguage !== 'undefined') {
    variableEntry.formatLanguage = formatLanguage;
  }
  if (linkType && linkType !== 'undefined') {
    variableEntry.linkType = linkType as FormLinkType;
  }
  if (linkLabel && linkLabel !== 'undefined') {
    variableEntry.linkLabel = linkLabel;
  }
  return { variableName, variableEntry };
};

export const transformOutput = (text: string) => {
  const parser = new DOMParser();
  const html = parser.parseFromString(text, 'text/html');
  const variableElements = [...html.getElementsByTagName(tag)];
  const variables = {} as FieldMappingVariables;
  variableElements.forEach((element) => {
    const { variableName, variableEntry } = pillToVariable(element, variables);
    element.replaceWith(`{{ ${variableName} }}`);
    variables[variableName] = variableEntry;
  });

  return { value: html.body.innerHTML, variables };
};

const pillTagRegex = new RegExp(`<${tag}\\b[^>]*></${tag}>`, 'g');

// A json (plaintext) editor emits getText(), so the value can't round-trip
// through innerHTML like transformOutput does — that would HTML-encode & and turn
// a "<" into a tag, corrupting the JSON. Instead swap the pill tags to
// {{ variables }} by string replacement and leave the rest of the text verbatim.
export const transformJsonOutput = (text: string) => {
  const parser = new DOMParser();
  const variables = {} as FieldMappingVariables;
  const value = text.replace(pillTagRegex, (pill) => {
    const element = parser.parseFromString(pill, 'text/html').querySelector(tag);
    if (!element) {
      return pill;
    }
    const { variableName, variableEntry } = pillToVariable(element, variables);
    variables[variableName] = variableEntry;
    return `{{ ${variableName} }}`;
  });

  return { value, variables };
};

const SIMPLE_PLACEHOLDER_TAGS = [
  'number-placeholder',
  'date-placeholder',
  'email-placeholder',
  'phone-placeholder',
];

/**
 * Returns the raw stored value for a manual default (no variables, plain text),
 * or null if the default is variable-mapped or empty. Use a `!= null` check at
 * the call site when only a presence test is needed.
 */
export const getManualDefaultValue = (
  mappedDefault: FieldMappingEditorValue | undefined | null
): string | null => {
  if (!mappedDefault || typeof mappedDefault === 'string') return null;
  if (!mappedDefault.value) return null;
  if (mappedDefault.variables && Object.keys(mappedDefault.variables).length > 0) return null;
  return mappedDefault.value;
};

/**
 * Replaces simple-placeholder elements with their `value` attribute as plain
 * text. Used on save so storage is the raw value (e.g. "10000") rather than
 * the editor's `<number-placeholder value="10000" label="10,000">` HTML.
 */
export const stripPlaceholdersToValue = (
  value: FieldMappingEditorValue
): FieldMappingEditorValue => {
  if (!value.value) return value;
  if (!SIMPLE_PLACEHOLDER_TAGS.some((tag) => value.value.includes(`<${tag}`))) return value;
  const html = new DOMParser().parseFromString(value.value, 'text/html');
  SIMPLE_PLACEHOLDER_TAGS.forEach((tag) => {
    [...html.getElementsByTagName(tag)].forEach((element) => {
      element.replaceWith(element.getAttribute('value') ?? '');
    });
  });
  return { ...value, value: html.body.innerHTML };
};

// Re-exported for existing consumers; the implementation lives in the pure
// (React-free) sibling module so the action previews can share it in node tests.
export { getVariableLabel, isTravellerVariableSource, labelSeparator };

// A field-mapping variable, e.g. {{ traveller_name }}. Group 1 is the name.
const DOUBLE_BRACE_VARIABLE = /\{\{\s*([^}]+?)\s*\}\}/g;
// The single-brace form { traveller_name }; the guards skip {{ }} pairs.
const SINGLE_BRACE_VARIABLE = /(?<!\{)\{\s*([^}]+?)\s*\}(?!\})/g;
// Either form. Group 1 holds the {{ }} name, group 2 the { } name — only one
// matches per hit.
const DOUBLE_OR_SINGLE_BRACE_VARIABLE = /\{\{\s*([^}]+?)\s*\}\}|\{\s*([^}]+?)\s*\}/g;

export const normalizeVariables = (
  template?: string,
  options?: { shouldParseSingleBraceVariables?: boolean }
): string => {
  if (!template) {
    return '';
  }
  const normalized = template.replace(DOUBLE_BRACE_VARIABLE, (_, key) => `{{ ${key.trim()} }}`);
  // JSON uses single braces structurally, so its editor opts out — collapsing
  // them here would strip the formatting.
  if (options?.shouldParseSingleBraceVariables === false) {
    return normalized;
  }
  return normalized.replace(SINGLE_BRACE_VARIABLE, (_, key) => `{ ${key.trim()} }`);
};

export const transformInput = ({
  value,
  variables,
  variableGroups,
  shouldParseSingleBraceVariables = true,
}: FieldMappingEditorValue & {
  variableGroups?: FieldMappingGroup[];
  shouldParseSingleBraceVariables?: boolean;
}) => {
  let parsedValue = normalizeVariables(value, { shouldParseSingleBraceVariables });

  if (variables) {
    // JSON opts out of single-brace so its structural braces aren't treated as variables.
    const variablePattern = shouldParseSingleBraceVariables
      ? DOUBLE_OR_SINGLE_BRACE_VARIABLE
      : DOUBLE_BRACE_VARIABLE;

    parsedValue = parsedValue.replace(
      variablePattern,
      (match, doubleVariableName, singleVariableName) => {
        const variableName = doubleVariableName || singleVariableName;
        const variable = variables[variableName];
        if (!variable) return match;

        const { options, optionsFromListId, excludedOptions, icon, iconRight, ...val } = variable;
        const variableLabel = getVariableLabel(val, variableGroups);

        return getPlaceholderHtml({
          ...val,
          variable: variableName,
          label: variableLabel,
          format: val.format ?? '',
          formatLanguage: val.formatLanguage ?? '',
          linkType: val.linkType ?? '',
          linkLabel: val.linkLabel ?? '',
          isActive: val.isActive ? val.isActive.toString() : '',
        });
      }
    );
  }

  return { value: parsedValue };
};

export const transformSimpleValueInput = (
  input: ExtendedFieldMappingEditorValue[],
  transformValue: (value: string) => string
) =>
  input.reduce(
    (accumulated: FieldMappingEditorValue, current) => {
      if (isFieldMappingEditorValue(current)) {
        accumulated.value = `${accumulated.value}${current.value}`;
        accumulated.variables = { ...accumulated.variables, ...current.variables };
      } else {
        accumulated.value = `${accumulated.value}${transformValue(current.value)}`;
      }
      return accumulated;
    },
    { value: '', variables: {} }
  );

export const transformSimpleValueOutput = (
  fieldMappingValue: FieldMappingEditorValue,
  placeholderTag: string
) => {
  const parser = new DOMParser();
  const html = parser.parseFromString(fieldMappingValue.value, 'text/html');
  const elements = [...html.getElementsByTagName(placeholderTag)];
  const output = fieldMappingValue.variables
    ? Object.entries(fieldMappingValue.variables).map((variable) => ({
        value: `{{ ${variable[0]} }}`,
        variables: { [variable[0]]: variable[1] },
      }))
    : ([] as ExtendedFieldMappingEditorValue[]);
  elements.forEach((element) => {
    const value = element.attributes.getNamedItem('value')?.nodeValue ?? '';
    output.push({ value });
  });
  return output;
};

export const getPlaceholderNodes = (state: EditorState | Transaction) => {
  const NATIVE_TYPES = ['paragraph', 'text'];
  const placeholderNodes: Node[] = [];
  state.doc.descendants((node) => {
    if (!NATIVE_TYPES.includes(node.type.name)) {
      placeholderNodes.push(node);
    }
  });
  return placeholderNodes;
};

export const extractName = (variable: MappableVariable) => {
  const variableName =
    variable.source === 'action'
      ? `${variable.action}_${variable.field}`
      : variable.source?.startsWith('journey_attribute__') ||
        variable.source?.startsWith('company_traveller_attribute__')
      ? `${variable.source}__${variable.field}`
      : `${variable.source}_${variable.field}`;
  return variableName;
};

export const getFieldContent = (label: string | { value: string }): string => {
  if (typeof label === 'string') return label;
  if (typeof label === 'object' && label !== null && 'value' in label) {
    return label.value;
  }
  return '';
};

/**
 * Maps variable groups into a flat record of variables by name
 */
export const mapVariableGroups = (variableGroups: FieldMappingGroup[] | undefined) => {
  const variables = {} as Record<string, MappableVariable>;
  if (!variableGroups) {
    return variables;
  }
  variableGroups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.isActive !== false) {
        if (field.type !== 'separator') {
          let variableName = '';
          if (field.field === 'unique_link') {
            variableName = 'action_unique_link';
          } else {
            variableName = extractName(field as MappableVariable);
          }
          variables[variableName] = field as MappableVariable;
        }
      }
    });
  });
  return variables;
};

export const templatePattern = /{{\s*(\w+)\s*}}|{\s*(\w+)\s*}/g;

/**
 * Removes unknown variables from text (variables not in the provided variables map)
 */
export const removeUnknownVariables = (text: string, variables: Record<string, any>) => {
  return text.replace(templatePattern, (match, partOne, partTwo) => {
    const variableName = partOne || partTwo;
    return variables.hasOwnProperty(variableName) ? match : '';
  });
};

/**
 * Finds variables in text that are not in the provided variables map
 */
export const findUnknownVariables = (text: string, variables: Record<string, any>): string[] => {
  const matches = [...text.matchAll(templatePattern)];

  const unknowns = new Set(
    matches
      .map((match) => match[1] || match[2])
      .filter((variableName) => !variables.hasOwnProperty(variableName))
  );

  return Array.from(unknowns);
};

type UseVariableStyledContentOptions = {
  /** The raw text content that may contain variable placeholders */
  content: string;
  variables: Record<string, MappableVariable>;
  variableGroups?: FieldMappingGroup[];
  icon?: typeof BaseIcon;
  hideRemove?: boolean;
  /** Whether to include full variable attributes in node (source, field, action, type) */
  includeFullAttrs?: boolean;
  suffix?: string;
  isEditMode?: boolean;
  onRemove?: () => void;
  enableExpandable?: boolean;
  showFieldMappingSidebar?: (props?: { variable?: FieldMappingFieldItem }) => void;
};

/**
 * Hook that transforms text with variable placeholders into styled content with blue tokens.
 * Use this when you need to display text that contains {{ variable }} placeholders.
 */
export const useVariableStyledContent = ({
  content,
  variables,
  variableGroups,
  icon: Icon = IconMappingOn,
  hideRemove,
  includeFullAttrs = true,
  suffix = '',
  isEditMode = false,
  onRemove,
  enableExpandable = false,
  showFieldMappingSidebar,
}: UseVariableStyledContentOptions) => {
  const cleanMessage = removeUnknownVariables(content, variables);
  const withSuffix = suffix ? `${cleanMessage}${suffix}` : cleanMessage;
  const text = withSuffix.replace(/\{\{\s*/g, '{').replace(/\s*\}\}/g, '}') || ' ';

  const { styledTranslate } = useScopedTranslate({ en: { text } });

  const resolvedIsEditMode = onRemove ? true : isEditMode || enableExpandable;
  const resolvedHideRemove = hideRemove ?? !onRemove;

  const contextValue = {
    showFieldMappingSidebar: showFieldMappingSidebar ?? (() => {}),
    variableGroups: variableGroups ?? [],
    isEditMode: resolvedIsEditMode,
    hasFormatting: false,
    disableExpandable: !enableExpandable,
  };

  const mockEditor = {
    isEditable: false,
    view: { dom: { closest: () => null } },
  } as unknown as NodeViewProps['editor'];

  const variablesMap = Object.entries(variables).reduce((acc, [variableKey, variable]) => {
    const variableLabel = getVariableLabel(variable, variableGroups);
    const actionId = 'action' in variable ? variable.action : undefined;
    const isMissing = isVariableSourceMissing({ source: variable.source, action: actionId ?? '' });
    const variant = isMissing ? 'fieldMappedError' : 'fieldMapped';
    const icon = isMissing ? IconMappingOff : Icon;
    acc[variableKey] = (
      <FieldMappingContext.Provider value={contextValue}>
        <PlaceholderContainer
          variant={variant}
          deleteNode={onRemove ?? (() => {})}
          editor={mockEditor}
          getPos={() => 0}
          icon={icon}
          hideRemove={resolvedHideRemove}
          // @ts-ignore-next-line
          node={{
            attrs: includeFullAttrs
              ? {
                  label: variableLabel,
                  source: variable.source,
                  field: variable.field,
                  action: actionId,
                  type: variable.type,
                  variable: variableKey,
                }
              : { label: variableLabel },
          }}
        />
      </FieldMappingContext.Provider>
    );
    return acc;
  }, {} as Record<string, JSX.Element>);

  return {
    cleanMessage,
    styledContent: styledTranslate('text', variablesMap),
  };
};
